<template>

<div class="list-view">

	<div class="sub-header">
		<div class="input-with-control">
			<input type="search" ref="search_term" v-model="filters.search_term" placeholder="Search..." @search="getList()" v-focus>
			<div class="button action input-go" @click="getList()"><i class="fas fa-search"></i></div>
		</div>
		<div class="controls">
			<pagination v-if="paging.total_pages > 1" :paging="paging" @updatePage="goToPage"></pagination>
		</div>
	</div>

	<div class="filters">

		<div v-if="!showDetail">

			<div class="label-group">
				<label>Start</label>
				<input type="date" v-model="filters.start_date" :max="$today().clone().format('YYYY-MM-DD')">
			</div>
			<div class="label-group">
				<label>End</label>
				<input type="date" v-model="filters.end_date" :max="$today().clone().format('YYYY-MM-DD')">
			</div>
			<div class="label-group">
				<label></label>
				<div class="button small neutral" @click="getList()">Filter Dates</div>
			</div>
			<div class="divider"></div>
			<select v-model="filters.user_type_id" @change="getList()">
				<option value="">Employee Type...</option>
				<option v-for="(item, index) in userTypes" :value="item.user_type_id">{{ item.description }}</option>
			</select>

			<select v-model="filters.user_id" @change="getList()">
				<option value="">Employee...</option>
				<option v-for="(item, index) in users" :value="item.user_id">{{ item.full_name }}</option>
			</select>

			<ul class="checkbox-group">
				<li>
					<span :class="['pointer', 'far', filters.approved ? 'fa-check-square' : 'fa-square']" @click="toggleFilter('approved')"></span>
					<label>Include closed time sheets</label>
				</li>
			</ul>

			<div class="divider"></div>
			<div class="button neutral" @click="clearFilters()">Clear Filters</div>
			<div class="divider"></div>
			<div class="extra-menu-item" @click="showEnableModal = true">{{ timeSheetsEnabled ? 'Dea' : 'A' }}ctivate Time Sheets</div>

		</div>

		<div v-if="showDetail">
			<div v-if="detail.approved" class="button action" @click="printDetail()">Print Time Sheet</div>
			<div v-if="detail.approved" class="button neutral" @click="showReopenModal = true">Reopen Time Sheet</div>
			<div v-else :class="['button', hasSelected ? 'action' : 'neutral']" @click="confirmApprove()">Approve Selected</div>
			<div v-if="!detail.approved" class="button neutral" @click="editItem({})">Add an Entry</div>
			<div class="button neutral" @click="getList()">Back To List</div>
		</div>

	</div>

	<div v-if="showDetail" class="detail generic-detail">
        <div class="detail-heading">
			<div><label>Employee</label><strong>{{ detail.full_name }}</strong></div>
			<div><label>Employee Type</label>{{ detail.user_type }}</div>
			<div><label>Timesheet Start</label>{{ $formatDate(detail.start_date) }}</div>
			<div><label>Timesheet End</label>{{ $formatDate(detail.end_date) }}</div>
			<div><label>Status</label>{{ detail.approved ? 'Closed' : 'Open' }}</div>
			<div class="right"><label>Total Hours</label><strong>{{ detail.total_hours }}</strong></div>
        </div>
		<div>
			<table class="clickable-list">
				<thead>
					<tr>
						<th v-if="!detail.approved"></th>
						<th>System</th>
						<th>Action</th>
						<th>Description</th>
						<th>Date</th>
						<th>Time</th>
						<th>Hours</th>
						<th v-if="!detail.approved"></th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="(item, index) in detail.entries" :class="item.hours && !detail.approved ? 'clickable-list-item' : 'list-item'" style="line-height: 1.25em;">
						<td v-if="!detail.approved" :class="['list', { 'dark-border': item.hours }]" @click="item.selected = !item.selected">
							<i v-if="item.hours" :class="['pointer', 'far', item.selected ? 'fa-check-square' : 'fa-square']" style="font-size: 1.25em; color: #666;" />
						</td>
						<td :class="['list', { 'dark-border': item.hours }]" @click="item.selected = !item.selected">{{ item.project }}</td>
						<td :class="['list', { 'dark-border': item.hours }]" @click="item.selected = !item.selected">{{ item.action.toUpperCase()}}</td>
						<td :class="['list', { 'dark-border': item.hours }]" @click="item.selected = !item.selected">{{ item.description }}</td>
						<td :class="['list', { 'dark-border': item.hours }]" @click="item.selected = !item.selected">{{ item.formatted_date }}</td>
						<td :class="['list', { 'dark-border': item.hours }]" @click="item.selected = !item.selected">{{ item.formatted_time }}</td>
						<td :class="['list', { 'dark-border': item.hours }]" @click="item.selected = !item.selected">{{ item.hours }}</td>
						<td v-if="!detail.approved" :class="['list', 'center', { 'dark-border': item.hours }]" @click="editItem(item)"><i class="pointer fa fa-edit" style="color: #666;" /></td>
					</tr>
				</tbody>
			</table>
		</div>
    </div>

	<div v-else class="list report-list">

	    <div v-if="list.length == 0" class="empty-list">
	        This list is empty
	    </div>

	    <table v-else class="data-list" cellpadding="0" cellspacing="0">
			<thead>
				<tr>
					<th class="list">Employee</th>
					<th class="list">Type</th>
					<th class="list">Start</th>
					<th class="list">End</th>
					<th class="list right">Days</th>
					<th class="list right">Hours</th>
					<th class="list center">In / Out</th>
					<th class="list center">Closed</th>
				</tr>
			</thead>
			<tr v-for="item in list" class="clickable-list-item" @click="getItem(item)">
				<td class="list">{{ item.full_name }}</td>
				<td class="list">{{ item.user_type }}</td>
				<td class="list">{{ $formatDate(item.start_date) }}</td>
				<td class="list">{{ $formatDate(item.end_date) }}</td>
				<td class="list right">{{ item.days }}</td>
				<td class="list right">{{ item.total_hours }}</td>
				<td class="list center"><i :class="['fa', item.days > item.days_clocked_out ? 'fa-times error' : 'fa-check']" /></td>
                <td class="list center"><i v-if="item.approved" class="fa fa-check" /></td>
			</tr>
		</table>

	</div>

	<message-modal v-if="message" :message="message" :loading="loading" :errorStatus="messageStatus" :timeout="messageTimeout" @dismiss="message = ''" />

	<iframe id="download" src="" style="display: none;"></iframe>

	<div v-if="showModal" class="modal">
		<div class="inner">
			<h2>{{ editEntry.time_sheet_entry_id ? 'Edit' : 'Add' }} A Time Sheet Entry</h2>
			<div class="content">
				<p class="center">
					<input type="radio" name="action" value="in" v-model="editEntry.action" /> Clock In
					<input type="radio" name="action" value="out" v-model="editEntry.action" /> Clock Out
				</p>
				<label>Description</label>
				<input type="text" v-model="editEntry.description" />
				<div class="two-columns">
					<div>
						<label>Date</label>
						<input type="date" v-model="editEntry.work_date" />
					</div>
					<div>
						<label>Time</label>
						<input type="time" v-model="editEntry.translated_time" />
					</div>
				</div>
				<div v-if="modalMessage" class="message-overlay">
					<p>{{ modalMessage }}</p>
					<p v-if="modalSuccess"><i class="fa fa-check" /></p>
				</div>
			</div>
			<div class="controls">
				<div class="button cancel spaced" @click="cancelEdit()">Cancel</div>
				<div v-if="!confirmDelete" :class="['button', 'spaced', saving ? 'disabled' : 'cancel']" @click="deleteEntry()"><i class="fa fa-trash" /></div>
				<div v-if="!confirmDelete" :class="['button', 'spaced', saving ? 'disabled' : 'action']" @click="saveEntry()">Save Entry</div>
				<div v-if="confirmDelete" :class="['button', 'spaced', saving ? 'disabled' : 'delete']" @click="saveEntry(true)">Yes, Delete</div>
			</div>
		</div>
	</div>

	<div v-if="showApproveModal" class="modal">
		<div class="inner">
			<h2>Approve Selected Hours</h2>
			<div class="content">
				<p>You are approving total hours of <b>{{ approveDetail.formattedHours }}</b> for employee <b>{{ detail.full_name }}</b>.</p>
				<p>Selected sets of entries will be saved to a "closed" time sheet.<br>Any unselected sets will be kept on an "open" time sheet.</p>
				<p>Once approved, you can print a statement for this employee.</p>
				<div v-if="modalMessage" class="message-overlay">
					<p>{{ modalMessage }}</p>
					<p v-if="modalSuccess"><i class="fa fa-check" /></p>
				</div>
			</div>
			<div class="controls">
				<div class="button cancel spaced" @click="cancelApprove()">Cancel</div>
				<div :class="['button', 'spaced', saving ? 'disabled' : 'action']" @click="saveApproved()">Approve</div>
			</div>
		</div>
	</div>

	<div v-if="showReopenModal" class="modal">
		<div class="inner">
			<h2>Reopen Time Sheet</h2>
			<div class="content">
				<p>You are reopening a time sheet for employee <b>{{ detail.full_name }}</b>.</p>
				<p>Once reopened you will be able to edit time sheet entries and then approve the modified time sheet.</p>
				<div v-if="modalMessage" class="message-overlay">
					<p>{{ modalMessage }}</p>
					<p v-if="modalSuccess"><i class="fa fa-check" /></p>
				</div>
			</div>
			<div class="controls">
				<div class="button cancel spaced" @click="showReopenModal = false">Cancel</div>
				<div :class="['button', 'spaced', saving ? 'disabled' : 'action']" @click="saveReopen()">Reopen</div>
			</div>
		</div>
	</div>

	<div v-if="showEnableModal" class="modal">
		<div class="inner">
			<h2>{{ timeSheetsEnabled ? 'Dea' : 'A' }}ctivate Time Sheets</h2>
			<div class="content">
				<p>Employee Time Sheets are currently <b>{{ timeSheetsEnabled ? 'a' : 'dea' }}ctivated</b>.</p>
				<p v-if="timeSheetsEnabled">When deactivated, the Clock In / Clock Out button will be hidden on all employee and cashier log in screens.</p>
				<p v-else>When activated, the Clock In / Clock Out button will be available on all employee and cashier log in screens.</p>
				<div v-if="modalMessage" class="message-overlay">
					<p>{{ modalMessage }}</p>
					<p v-if="modalSuccess"><i class="fa fa-check" /></p>
				</div>
			</div>
			<div class="controls">
				<div class="button cancel spaced" @click="showEnableModal = false">Cancel</div>
				<div :class="['button', 'spaced', saving ? 'disabled' : 'action']" @click="saveEnable()">{{ timeSheetsEnabled ? 'Dea' : 'A' }}ctivate</div>
			</div>
		</div>
	</div>

	<help-popup :helpLink="{378:'Employee Timesheets'}" :tours="false"></help-popup>

</div>

</template>

<script>
import HelpPopup from '@/components/HelpPopup.vue'

export default {
	name: 'employee-time-sheets',
	components: {
		'help-popup': HelpPopup
    },
	data() {
		return {
            loading: false,
			message: '',
			messageStatus: '',
			messageTimeout: 0,
            users: [],
			userTypes: [],
			filters: {},
			list: [],
			timeSheetsEnabled: '',
			paging: {},
			showDetail: false,
			detail: {},
			exportMessage: '',
			gettingExport: false,
			showModal: false,
			editEntry: {},
			modalMessage: '',
			modalSuccess: false,
			saving: false,
			approveDetail: {},
			showApproveModal: false,
			showReopenModal: false,
			showEnableModal: false,
			confirmDelete: false
		}
	},
	created() {
		this.clearFilters()
        this.getUsers()
        this.getUserTypes()
	},
	computed: {
		hasSelected() {
			return this.detail.entries.filter(entry => entry.hours && entry.selected).length > 0
		}
	},
	methods: {
		clearFilters() {
			this.filters = {
				page: 1,
				search_term: '',
				start_date: '',
				end_date: '',
                user_type_id: '',
                user_id: '',
                approved: false,
				do_export: false
			}
			this.getList()
		},
        getUsers() {
			this.apiCall({ destination: 'get_list_users', data: { manager_flag: 1 } }).then(obj => {
                if (obj.status == 'success') {
					this.users = obj.users
				}
			}).catch(err => {
				console.log(err)
			})
        },
        getUserTypes() {
            this.apiCall({ destination: 'get_list_user_types', data: { manager_flag: 1 } }).then(obj => {
                this.userTypes = obj.user_types
            }).catch(err => {
                console.log(err);
            })
        },
		getList() {
			if (this.loading) { return }
			this.showDetail = false
			this.loading = true
			this.message = 'Generating report...'
			this.apiCall({ destination: 'get_list_time_sheets', data: this.filters }).then(obj => {
				this.loading = false
				this.message = obj.message
				this.messageStatus = obj.status
				if (this.filters.do_export) {
					this.filters.do_export = 0
					var outputFile = obj.file_name
					// accommodate live server filecache latency
					setTimeout(() => {
						document.getElementById("download").src = "//" + document.location.hostname + "/download.php?file_name=" + obj.file_name + "&output_file_name=" + outputFile
						this.gettingExport = false
						this.exportMessage = ''
					}, 1500)
				} else {
					this.list = obj.list
					this.paging = obj.paging
					this.timeSheetsEnabled = obj.enable_time_sheets
				}
			}).catch(err => {
				console.log(err)
			})
		},
		goToPage(page) {
			this.filters.page = page
			this.getList()
		},
		toggleFilter(which) {
			this.filters[which] = !this.filters[which]
			this.getList()
		},
		saveEnable() {
			if (this.saving) { return }
			this.saving = true
			this.modalMessage = (this.timeSheetsEnabled ? 'Deactivating' : 'Activating' ) + ' employee time sheets...'
			this.modalSuccess = false
			let newPreferenceValue = this.timeSheetsEnabled ? '' : '1'
			this.apiCall({ destination: 'save_item_preference', data: { preference_code: 'ENABLE_TIME_SHEETS', value: newPreferenceValue } }).then(obj => {
				this.saving = false
				if (obj.status == 'success') {
					this.modalMessage = 'Employee time sheets ' + (newPreferenceValue ? 'activated' : 'deactivated' ) + '!'
					this.modalSuccess = true
					setTimeout(() => {
						this.showEnableModal = false
						this.modalMessage = ''
						this.modalSuccess = false
						this.timeSheetsEnabled = newPreferenceValue
					}, 1500)
				} else {
					this.modalMessage = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		getItem(item) {
			if (this.loading) { return }
			this.loading = true
			this.apiCall({ destination: 'get_item_time_sheet', data: { time_sheet_id: item.time_sheet_id } }).then(obj => {
				this.loading = false
				this.detail = obj.time_sheet
                this.showDetail = true
			}).catch(err => {
				console.log(err)
			})
		},
		editItem(item) {
			this.editEntry = item
			if (!this.editEntry.time_sheet_id) {
				this.editEntry.time_sheet_id = this.detail.time_sheet_id
			}
			this.modalMessage = ''
			this.modalSuccess = false
			this.showModal = true
		},
		saveEntry(doDelete) {
			if (this.saving) { return }
			if (!this.editEntry.work_date || !this.editEntry.translated_time) {
				this.modalMessage = 'Enter missing value'
				setTimeout(() => {
					this.modalMessage = ''
				}, 1500)
				return
			}
			this.saving = true
			this.modalMessage = 'Saving time sheet entry...'
			this.modalSuccess = false
			this.editEntry.do_delete = doDelete
			this.apiCall({ destination: 'save_item_time_sheet_entry', data: this.editEntry }).then(obj => {
				this.saving = false
				if (obj.status == 'success') {
					this.getItem(this.detail)
					this.modalMessage = 'Time sheet entry ' + (doDelete ? 'deleted' : 'saved') + '!'
					this.modalSuccess = true
					setTimeout(() => {
						this.showModal = false
						this.modalMessage = ''
						this.modalSuccess = false
						this.confirmDelete = false
					}, 1500)
				} else {
					this.modalMessage = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		deleteEntry() {
			this.modalMessage = 'Are you sure you want to delete this entry?'
			this.modalSuccess = false
			this.confirmDelete = true
		},
		cancelEdit() {
			this.editEntry = {}
			this.showModal = false
			this.confirmDelete = false
		},
		confirmApprove() {
			if (!this.hasSelected) { return }
			this.modalMessage = ''
			this.modalSuccess = false
			this.approveDetail = {
				minutes: 0,
				formattedHours: ''
			}
			let selectedEntries = this.detail.entries.filter(entry => entry.hours && entry.selected)
			let totalMinutes = 0
			selectedEntries.forEach(function(item) {
				totalMinutes += item.minutes
			})
			this.approveDetail.formattedHours = parseInt(totalMinutes / 60) + ':' + (totalMinutes % 60).toString().padStart(2, '0');
			this.showApproveModal = true
		},
		cancelApprove() {
			this.showApproveModal = false
		},
		saveApproved() {
			if (this.saving) { return }
			this.saving = true
			this.modalMessage = 'Approving time sheet...'
			this.modalSuccess = false
			let approvedEntries = this.detail.entries.filter(entry => entry.hours && entry.selected)
			this.apiCall({ destination: 'save_item_time_sheet', data: { time_sheet_id: this.detail.time_sheet_id, entries: approvedEntries } }).then(obj => {
				this.saving = false
				if (obj.status == 'success') {
					this.getItem(this.detail)
					this.modalMessage = 'Time sheet approved!'
					this.modalSuccess = true
					setTimeout(() => {
						this.showApproveModal = false
						this.modalMessage = ''
						this.modalSuccess = false
					}, 1500)
				} else {
					this.modalMessage = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		saveReopen() {
			if (this.saving) { return }
			this.saving = true
			this.modalMessage = 'Reopening time sheet...'
			this.modalSuccess = false
			this.apiCall({ destination: 'save_item_time_sheet_reopen', data: { time_sheet_id: this.detail.time_sheet_id } }).then(obj => {
				this.saving = false
				if (obj.status == 'success') {
					this.getItem(this.detail)
					this.modalMessage = 'Time sheet reopened!'
					this.modalSuccess = true
					setTimeout(() => {
						this.showReopenModal = false
						this.modalMessage = ''
						this.modalSuccess = false
					}, 1500)
				} else {
					this.modalMessage = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		printDetail() {
			window.print()
		}
	}
}
</script>

<style lang="scss" scoped>
</style>
